/**
 * @module ui/Tooltip
 * @description Handles the positioning of a "target" element relative to a
 * sibling element (e.g. a Dropdown Menu relative to its "trigger" button)
 */
import { getById, qs } from "utils/utils";
import PositionHelper from "utils/PositionHelper";
import logger from "utils/logger";

const baseClass = "c-tooltip";
const debugTagBase = "[TOOLTIP]";

/**
 * @class
 */
export default class Tooltip {

  /**
   * Initialize the tooltip
   * @param {HTMLElement} triggerEl - The tooltip's trigger element
   * @param {Object} [options={}] - Options for the instance
   * @param {Boolean} options.autoAdjustHorizontal - Whether to automatically adjust the position along the horizontal axis
   * @param {Boolean} options.autoAdjustVertical - Whether to automatically adjust the position along the vertical axis
   * @param {Number} [options.closeDelay=4000] - Polling interval for checking whether the Tooltip should remain open
   */
  constructor(triggerEl, options = {}) {
    if (!triggerEl) {
      return;
    }
    const tooltipID = triggerEl.getAttribute("aria-describedby");
    this.el = getById(tooltipID);

    if (!this.el || !this.el.classList.contains(baseClass)) {
      return;
    }
    // Just in case the "hidden" attribute isn't already present on the Tooltip
    this.el.setAttribute("hidden", "hidden");
    this.trigger = triggerEl;
    this.onBodyKeydownFn = this.onBodyKeydown.bind(this);
    this.openFn = this.open.bind(this);
    this.closeFn = this.close.bind(this);
    this.closeInterval = 0;
    this.onTransitionEndFn = this.onTransitionEnd.bind(this);
    this.options = Object.assign({ closeDelay: 4000 }, options);

    this.position = new PositionHelper(this.trigger, this.el, baseClass, options);

    this.debugTag = `${debugTagBase}[${tooltipID}]`;
    logger.info(`${this.debugTag} init`);
  }

  /**
   * Activate the tooltip by attaching the relevant event listeners
   */
  activate() {
    logger.info(`${this.debugTag} activate`);
    document.addEventListener("keydown", this.onBodyKeydownFn, true);

    this.trigger.addEventListener("focus", this.openFn);
    this.trigger.addEventListener("blur", this.closeFn);

    if (this.isHoverEnabled()) {
      this.trigger.addEventListener("mouseenter", this.openFn);
      this.trigger.addEventListener("mouseleave", this.closeFn);

      this.el.addEventListener("mouseenter", this.openFn);
      this.el.addEventListener("mouseleave", this.closeFn);
    }
    this.el.addEventListener("transitionend", this.onTransitionEndFn, false);
  }

  /**
   * Deactivate the tooltip by removing the relevant event listeners
   */
  deactivate() {
    logger.info(`${this.debugTag} deactivate`);
    document.removeEventListener("keydown", this.onBodyKeydownFn);

    this.trigger.removeEventListener("focus", this.openFn);
    this.trigger.removeEventListener("blur", this.closeFn);

    this.trigger.removeEventListener("mouseenter", this.openFn);
    this.trigger.removeEventListener("mouseleave", this.closeFn);

    this.el.removeEventListener("mouseenter", this.openFn);
    this.el.removeEventListener("mouseleave", this.closeFn);

    this.el.removeEventListener("transitionend", this.onTransitionEndFn);
  }

  /**
   * Determine whether mouse hover interactions are possible on the user's device
   *
   * Some touch-enabled devices will also try to emulate mouse events,
   * so both touch and mouse events will fire on touch:
   * @see https://www.html5rocks.com/en/mobile/touchandmouse/#toc-together
   * Normally, we'd be able to prevent the emulated mouse events from firing
   * after the touch events by calling "event.preventDefault()" inside a
   * "touchstart" event handler, but Windows tablets (e.g. Surface) have touch
   * events disabled by default. This means that "touchstart" will never fire,
   * but the emulated mouse events will. This makes it impossible to determine
   * whether the user tapped or clicked on the element.
   * @see https://github.com/MicrosoftEdge/Status/issues/336
   * @see https://stackoverflow.com/questions/33300962/touch-api-e-g-touchstart-not-working-in-ms-edge
   * @see https://caniuse.com/#search=touch
   * To make things even worse, when a user taps on an element, not only do the
   * "mousedown", "mouseup" and "click" events fire (which is to be expected),
   * but the "mouseenter" and "mouseleave" events fire too! This conflicts with
   * the dropdown menu's mouse hover functionality, so we've decided to avoid
   * enabling this functionality on devices that support multiple touchpoints.
   * @see https://hacks.mozilla.org/2013/04/detecting-touch-its-the-why-not-the-how/
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/maxTouchPoints
   * @returns {Boolean}
   */
  isHoverEnabled() {
    return !navigator.maxTouchPoints;
  }

  /**
   * Determine whether the tooltip is currently open/visible
   * @returns {Boolean}
   */
  isOpen() {
    const style = window.getComputedStyle(this.el);
    const opacity = Number(style.getPropertyValue("opacity"));
    return opacity > 0 && this.el.classList.contains(`${baseClass}--open`);
  }

  /**
   * Determine whether the tooltip is currently closed/hidden
   * @returns {Boolean}
   */
  isClosed() {
    const style = window.getComputedStyle(this.el);
    const opacity = Number(style.getPropertyValue("opacity"));
    return opacity < 1 && !this.el.classList.contains(`${baseClass}--open`);
  }

  /**
   * Adjust the tooltip's position, if necessary, then open/reveal it
   */
  open() {
    if (!this.isOpen()) {
      logger.info(`${this.debugTag} open`);

      this.el.removeAttribute("hidden");
      this.position.setPosition();
      this.el.classList.add(`${baseClass}--open`);

      // Every X seconds, check whether the Tooltip should remain open,
      // because sometimes the "mouseleave" event fails to fire and we
      // don't want the Tooltip to remain open indefinitely
      window.clearInterval(this.closeInterval);
      this.closeInterval = window.setInterval(() => {
        // Check whether the trigger element, one of its children,
        // the Tooltip, or one of its children is hovered or focused
        if (
          this.trigger.matches(":hover") ||
          this.trigger.matches(":focus") ||
          qs(":hover", this.trigger) ||
          qs(":focus", this.trigger) ||
          this.el.matches(":hover") ||
          this.el.matches(":focus") ||
          qs(":hover", this.el) ||
          qs(":focus", this.el)
        ) {
          logger.info(`${this.debugTag} should remain open`);
        } else {
          logger.info(`${this.debugTag} should close`);
          this.close();
        }
      }, this.options.closeDelay);
    }
  }

  /**
   * Close/hide the tooltip
   */
  close() {
    if (!this.isClosed()) {
      logger.info(`${this.debugTag} close`);
      this.el.classList.remove(`${baseClass}--open`);
      window.clearInterval(this.closeInterval);
    }
  }

  /**
   * Close the dropdown menu when the user presses the escape key
   * @param {KeyboardEvent} event - A keydown event
   */
  onBodyKeydown(event) {
    if (event.keyCode === 27 && this.isOpen()) {
      this.close();
    }
  }

  /**
   * Add a "hidden" attribute after the Tooltip's hide transition has completed
   * @param {TransitionEvent} event
   */
  onTransitionEnd(event) {
    if (event.propertyName === "opacity" && !this.el.classList.contains(`${baseClass}--open`)) {
      logger.info(`${this.debugTag} onTransitionEnd`);
      this.el.setAttribute("hidden", "hidden");
    }
  }

}
